// @ts-ignore - react-plotly.js doesn't have proper TypeScript types
import { useMemo } from 'react'
import Plot from 'react-plotly.js'
import { AnnotationConsistencyMetrics } from '../../types'

interface SequentialImageAnalysisProps {
  metrics: AnnotationConsistencyMetrics[]
  selectedObjectIndex?: number | null
}

export default function SequentialImageAnalysis({
  metrics,
  selectedObjectIndex,
}: SequentialImageAnalysisProps) {
  // Filter metrics by selected object if specified
  const filteredMetrics = useMemo(() => {
    let filtered = metrics
    if (selectedObjectIndex !== null && selectedObjectIndex !== undefined) {
      filtered = metrics.filter(m => m.object_index === selectedObjectIndex)
    }
    return filtered
  }, [metrics, selectedObjectIndex])

  // Sort metrics by annotation ID to show temporal progression
  const sortedMetrics = useMemo(() => {
    return [...filteredMetrics].sort((a, b) => a.annotation_id - b.annotation_id)
  }, [filteredMetrics])

  // Calculate moving average for trend analysis
  const movingAverage = (data: number[], windowSize = 5) => {
    const result: number[] = []
    for (let i = 0; i < data.length; i++) {
      const start = Math.max(0, i - Math.floor(windowSize / 2))
      const end = Math.min(data.length, i + Math.floor(windowSize / 2) + 1)
      const window = data.slice(start, end)
      result.push(window.reduce((sum, val) => sum + val, 0) / window.length)
    }
    return result
  }

  const bboxStdValues = sortedMetrics.map(m => m.bbox_std)
  const keypointStdValues = sortedMetrics.map(m => m.keypoint_std)
  const visibilityValues = sortedMetrics.map(m => m.visibility_metric)

  const bboxMA = movingAverage(bboxStdValues)
  const keypointMA = movingAverage(keypointStdValues)

  // Calculate stability metrics
  const stabilityMetrics = useMemo(() => {
    const calculateStability = (values: number[]) => {
      if (values.length < 2) return { variance: 0, trend: 0, coefficientOfVariation: 0 }

      const mean = values.reduce((sum, val) => sum + val, 0) / values.length
      const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length
      const stdDev = Math.sqrt(variance)
      const coefficientOfVariation = mean !== 0 ? (stdDev / mean) * 100 : 0

      // Simple linear regression for trend
      const n = values.length
      const sumX = Array.from({ length: n }, (_, i) => i).reduce((a, b) => a + b, 0)
      const sumY = values.reduce((a, b) => a + b, 0)
      const sumXY = values.reduce((sum, val, i) => sum + i * val, 0)
      const sumX2 = Array.from({ length: n }, (_, i) => i * i).reduce((a, b) => a + b, 0)

      const trend = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)

      return { variance, trend, coefficientOfVariation, mean, stdDev }
    }

    return {
      bbox: calculateStability(bboxStdValues),
      keypoint: calculateStability(keypointStdValues),
      visibility: calculateStability(visibilityValues),
    }
  }, [bboxStdValues, keypointStdValues, visibilityValues])

  return (
    <div className="space-y-6">
      {/* Show selected object info if filtered */}
      {selectedObjectIndex !== null && selectedObjectIndex !== undefined && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <p className="text-sm text-yellow-800">
            Showing analysis for{' '}
            <span className="font-semibold">Object {selectedObjectIndex + 1}</span> across{' '}
            {sortedMetrics.length} frames
          </p>
        </div>
      )}

      {/* Stability Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 relative group">
          <h4 className="text-sm font-medium text-blue-900 flex items-center gap-1">
            Bbox Stability
            <svg
              className="w-4 h-4 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </h4>
          <p className="text-2xl font-semibold text-blue-700">
            {Math.max(0, 100 - stabilityMetrics.bbox.coefficientOfVariation).toFixed(1)}%
          </p>
          <p className="text-xs text-blue-600">
            Mean STD: {stabilityMetrics.bbox.mean?.toFixed(2) || '0.00'}% ±{' '}
            {stabilityMetrics.bbox.stdDev?.toFixed(2) || '0.00'}%
          </p>
          <p className="text-xs text-blue-600">
            Trend: {stabilityMetrics.bbox.trend > 0 ? '↑' : '↓'}{' '}
            {Math.abs(stabilityMetrics.bbox.trend).toFixed(3)}%/frame
          </p>

          {/* Tooltip */}
          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10 w-64">
            <div className="bg-gray-900 text-white text-xs rounded p-3 shadow-lg">
              <p className="font-semibold mb-1">Bounding Box Stability</p>
              <p>Measures consistency of bounding box annotations across the sequence.</p>
              <p className="mt-1">• 100% = Perfectly stable (no variation)</p>
              <p>• 90%+ = Highly stable</p>
              <p>• 80-90% = Normal range</p>
              <p>• &lt;80% = Needs review</p>
              <p className="mt-2 text-xs border-t border-gray-700 pt-2">
                Calculated from all frames{' '}
                {selectedObjectIndex !== null && selectedObjectIndex !== undefined
                  ? 'for selected object'
                  : 'in the project'}
              </p>
              <div className="absolute top-full left-4 transform -translate-x-1/2">
                <div className="border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 relative group">
          <h4 className="text-sm font-medium text-green-900 flex items-center gap-1">
            Keypoint Stability
            <svg
              className="w-4 h-4 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </h4>
          <p className="text-2xl font-semibold text-green-700">
            {Math.max(0, 100 - stabilityMetrics.keypoint.coefficientOfVariation).toFixed(1)}%
          </p>
          <p className="text-xs text-green-600">
            Mean STD: {stabilityMetrics.keypoint.mean?.toFixed(2) || '0.00'}% ±{' '}
            {stabilityMetrics.keypoint.stdDev?.toFixed(2) || '0.00'}%
          </p>
          <p className="text-xs text-green-600">
            Trend: {stabilityMetrics.keypoint.trend > 0 ? '↑' : '↓'}{' '}
            {Math.abs(stabilityMetrics.keypoint.trend).toFixed(3)}%/frame
          </p>

          {/* Tooltip */}
          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10 w-64">
            <div className="bg-gray-900 text-white text-xs rounded p-3 shadow-lg">
              <p className="font-semibold mb-1">Keypoint Stability</p>
              <p>Measures consistency of keypoint positions across the sequence.</p>
              <p className="mt-1">• 100% = Perfectly stable keypoints</p>
              <p>• 90%+ = Smooth motion</p>
              <p>• 70-90% = Normal motion variation</p>
              <p>• &lt;70% = Rapid motion or inconsistent annotations</p>
              <p className="mt-2 text-xs border-t border-gray-700 pt-2">
                Calculated from all keypoints across all frames{' '}
                {selectedObjectIndex !== null && selectedObjectIndex !== undefined
                  ? 'for selected object'
                  : 'in the project'}
              </p>
              <div className="absolute top-full left-4 transform -translate-x-1/2">
                <div className="border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-amber-50 rounded-lg p-4 relative group">
          <h4 className="text-sm font-medium text-amber-900 flex items-center gap-1">
            Visibility Stability
            <svg
              className="w-4 h-4 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </h4>
          <p className="text-2xl font-semibold text-amber-700">
            {Math.max(0, 100 - stabilityMetrics.visibility.coefficientOfVariation).toFixed(1)}%
          </p>
          <p className="text-xs text-amber-600">
            Mean: {stabilityMetrics.visibility.mean?.toFixed(2) || '0.00'} ±{' '}
            {stabilityMetrics.visibility.stdDev?.toFixed(2) || '0.00'}
          </p>
          <p className="text-xs text-amber-600">
            Trend: {stabilityMetrics.visibility.trend > 0 ? '↑' : '↓'}{' '}
            {Math.abs(stabilityMetrics.visibility.trend).toFixed(3)}/frame
          </p>

          {/* Tooltip */}
          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10 w-64">
            <div className="bg-gray-900 text-white text-xs rounded p-3 shadow-lg">
              <p className="font-semibold mb-1">Visibility Stability</p>
              <p>Measures consistency of visibility judgments across annotators.</p>
              <p className="mt-1">• 100% = Perfect agreement on visibility</p>
              <p>• 90%+ = High agreement</p>
              <p>• 80-90% = Minor disagreements</p>
              <p>• &lt;80% = Significant annotator disagreement</p>
              <p className="mt-1 text-xs">
                Lower values indicate annotators disagree on whether keypoints are visible/inferred
              </p>
              <p className="mt-2 text-xs border-t border-gray-700 pt-2">
                Based on visibility metrics from all frames{' '}
                {selectedObjectIndex !== null && selectedObjectIndex !== undefined
                  ? 'for selected object'
                  : 'in the project'}
              </p>
              <div className="absolute top-full left-4 transform -translate-x-1/2">
                <div className="border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Series Analysis */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Temporal Consistency Analysis</h3>

        {/* Combined time series plot */}
        <Plot
          data={[
            {
              x: sortedMetrics.map((_, idx) => idx),
              y: bboxStdValues,
              type: 'scatter',
              mode: 'markers',
              name: 'Bbox STD',
              marker: { color: '#3b82f6', size: 6, opacity: 0.6 },
              hovertemplate: 'Frame %{x}<br>Bbox STD: %{y:.2f}%<extra></extra>',
            },
            {
              x: sortedMetrics.map((_, idx) => idx),
              y: bboxMA,
              type: 'scatter',
              mode: 'lines',
              name: 'Bbox MA',
              line: { color: '#3b82f6', width: 2 },
              hovertemplate: 'Frame %{x}<br>Bbox MA: %{y:.2f}%<extra></extra>',
            },
            {
              x: sortedMetrics.map((_, idx) => idx),
              y: keypointStdValues,
              type: 'scatter',
              mode: 'markers',
              name: 'Keypoint STD',
              marker: { color: '#10b981', size: 6, opacity: 0.6 },
              hovertemplate: 'Frame %{x}<br>Keypoint STD: %{y:.2f}%<extra></extra>',
            },
            {
              x: sortedMetrics.map((_, idx) => idx),
              y: keypointMA,
              type: 'scatter',
              mode: 'lines',
              name: 'Keypoint MA',
              line: { color: '#10b981', width: 2 },
              hovertemplate: 'Frame %{x}<br>Keypoint MA: %{y:.2f}%<extra></extra>',
            },
          ]}
          layout={{
            title: 'Consistency Over Time (Sequential Frames)',
            xaxis: {
              title: 'Frame Index',
              range: [-1, sortedMetrics.length],
            },
            yaxis: {
              title: 'Standard Deviation (%)',
              range: [0, Math.max(...bboxStdValues, ...keypointStdValues) * 1.1],
            },
            height: 400,
            showlegend: true,
            legend: { orientation: 'h', y: -0.2 },
            hovermode: 'x unified',
            paper_bgcolor: 'transparent',
            plot_bgcolor: 'transparent',
          }}
          config={{ displayModeBar: false }}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  )
}
